import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Check, ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import {
  API, IMG, BARRELS, STONES, BURNER_MEDIA, ENGRAVING, SHAPES,
  BASE_PRICE, WIND_GUARD_PRICE, INPUT_CLS, fmtCAD,
} from "../lib/site";
import { Reveal, Eyebrow, EmberField, GoldButton, EASE } from "../components/motion";
import { EngravingPreview, ENGRAVING_FONTS } from "../components/EngravingPreview";

const OptionCard = ({ selected, onClick, testId, title, desc, price, swatch }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`group relative w-full rounded-xl border p-5 text-left transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 ${
      selected
        ? "border-amber-500/70 bg-amber-950/25 shadow-ember-sm"
        : "border-stone-800 bg-[#141110] hover:border-amber-500/35"
    }`}
  >
    <span className="flex items-start justify-between gap-4">
      <span className="flex items-start gap-3">
        {swatch && (
          <span className="stone-swatch mt-0.5 h-10 w-10 shrink-0 rounded-full border border-white/10" style={{ background: swatch }} />
        )}
        <span>
          <span className="block font-serif text-base text-stone-100">{title}</span>
          {desc && <span className="mt-1 block text-xs leading-relaxed text-stone-400">{desc}</span>}
        </span>
      </span>
      <span className="flex shrink-0 flex-col items-end gap-2">
        <span className="font-mono text-[11px] tracking-wider text-amber-400">{price === 0 ? "Included" : `+ ${fmtCAD(price)}`}</span>
        <span className={`flex h-5 w-5 items-center justify-center rounded-full border transition-colors duration-200 ${selected ? "border-amber-400 bg-amber-500 text-stone-950" : "border-stone-700 text-transparent"}`}>
          <Check size={12} strokeWidth={3} />
        </span>
      </span>
    </span>
  </button>
);

const StepBlock = ({ index, title, hint, children }) => (
  <Reveal>
    <section className="rounded-2xl border border-stone-800/80 bg-[#100d0b] p-6 sm:p-8">
      <div className="mb-6 flex items-baseline gap-4">
        <span className="font-mono text-xs tracking-[0.25em] text-amber-500">0{index}</span>
        <div>
          <h2 className="font-serif text-xl text-stone-100 sm:text-2xl">{title}</h2>
          {hint && <p className="mt-1 text-xs text-stone-500">{hint}</p>}
        </div>
      </div>
      <div className="grid gap-3">{children}</div>
    </section>
  </Reveal>
);

export default function Configurator() {
  const [barrel, setBarrel] = useState(BARRELS[0]);
  const [stone, setStone] = useState(STONES[0]);
  const [media, setMedia] = useState(BURNER_MEDIA[0]);
  const [shape, setShape] = useState(SHAPES[0]);
  const [engraving, setEngraving] = useState(ENGRAVING[0]);
  const [engravingText, setEngravingText] = useState("");
  const [engravingFont, setEngravingFont] = useState(ENGRAVING_FONTS[0]);
  const [windGuard, setWindGuard] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const total = useMemo(
    () => BASE_PRICE + barrel.price + stone.price + media.price + shape.price + engraving.price + (windGuard ? WIND_GUARD_PRICE : 0),
    [barrel, stone, media, shape, engraving, windGuard]
  );

  const configuration = {
    Barrel: barrel.name,
    Tabletop: stone.name,
    Burner: `${shape.name} 65,000 BTU`,
    Media: media.name,
    Engraving: engraving.id === "none" ? "None" : `${engraving.name}${engravingText ? ` — "${engravingText}"` : ""}`,
    ...(engraving.id !== "none" ? { "Lettering Style": engravingFont.name } : {}),
    "Wind Guard": windGuard ? "Yes" : "No",
    "Estimated Investment": `${fmtCAD(total)} CAD`,
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone || undefined,
          inquiry_type: "bespoke-commission",
          message: form.message || `Commission request from the Bespoke Atelier — ${barrel.name}, ${stone.name} top, ${media.name}.`,
          configuration,
        }),
      });
      if (!res.ok) throw new Error("send failed");
      setDone(true);
      toast.success("Your commission request has been sent to the Cozy Ember atelier.");
    } catch {
      toast.error("Something interrupted the send. Please email info@cozyember.design — we would love to hear from you.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="relative overflow-hidden pb-28 pt-40">
      <EmberField count={14} />
      <div className="animate-ember-pulse absolute right-[8%] top-[12%] h-96 w-96 rounded-full bg-amber-600/10 blur-[120px]" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-10">
        <Reveal>
          <Eyebrow>The Bespoke Atelier</Eyebrow>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 className="mt-5 max-w-3xl font-serif text-4xl font-semibold leading-[1.08] tracking-tight text-stone-50 sm:text-5xl lg:text-6xl">
            Compose <span className="italic text-gold-gradient">your</span> Cozy Ember.
          </h1>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-stone-400 sm:text-lg">
            Five decisions, made at your pace. Watch your commission take shape — then send it to
            the workshop with one touch.
          </p>
        </Reveal>

        <div className="mt-16 grid items-start gap-10 lg:grid-cols-[1fr_400px]">
          <div className="space-y-8">
            <StepBlock index={1} title="Barrel Character" hint="The soul of the piece — authentic, decommissioned, full of history.">
              {BARRELS.map((b) => (
                <OptionCard key={b.id} testId={`config-barrel-${b.id}`} selected={barrel.id === b.id} onClick={() => setBarrel(b)} title={b.name} desc={b.desc} price={b.price} />
              ))}
            </StepBlock>

            <StepBlock index={2} title="Tabletop Stone" hint="Precision water-jet cut, hand-finished, sealed for Canadian seasons.">
              <div className="grid gap-3 sm:grid-cols-2">
                {STONES.map((s) => (
                  <OptionCard key={s.id} testId={`config-stone-${s.id}`} selected={stone.id === s.id} onClick={() => setStone(s)} title={s.name} desc={s.desc} price={s.price} swatch={s.swatch} />
                ))}
              </div>
            </StepBlock>

            <StepBlock index={3} title="Flame & Burner" hint="65,000 BTU stainless jet burner, CSA-compliant propane assembly.">
              <div className="grid gap-3 sm:grid-cols-2">
                {SHAPES.map((s) => (
                  <OptionCard key={s.id} testId={`config-shape-${s.id}`} selected={shape.id === s.id} onClick={() => setShape(s)} title={`${s.name} Burner`} price={s.price} />
                ))}
              </div>
              <div className="mt-3 grid gap-3">
                {BURNER_MEDIA.map((m) => (
                  <OptionCard key={m.id} testId={`config-media-${m.id}`} selected={media.id === m.id} onClick={() => setMedia(m)} title={m.name} desc={m.desc} price={m.price} />
                ))}
              </div>
            </StepBlock>

            <StepBlock index={4} title="Personalization" hint="Hand-burned into the staves — the mark that makes it unmistakably yours.">
              {ENGRAVING.map((en) => (
                <OptionCard key={en.id} testId={`config-engraving-${en.id}`} selected={engraving.id === en.id} onClick={() => setEngraving(en)} title={en.name} desc={en.desc} price={en.price} />
              ))}
              <AnimatePresence>
                {engraving.id !== "none" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.4, ease: EASE }}
                    className="overflow-hidden"
                  >
                    <div className="mt-4 rounded-xl border border-amber-900/30 bg-black/30 p-5">
                      <div className="grid gap-5 sm:grid-cols-[220px_1fr]">
                        <EngravingPreview text={engravingText} font={engravingFont} />
                        <div>
                          <label htmlFor="engraving-text" className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-500/90">
                            Engraving Text
                          </label>
                          <input
                            id="engraving-text"
                            data-testid="config-engraving-text"
                            value={engravingText}
                            onChange={(e) => setEngravingText(e.target.value.slice(0, 40))}
                            placeholder="e.g. The Wexford Estate · Lakeside Cottage · Crate's Marine"
                            className={`${INPUT_CLS} mt-3`}
                          />
                          <p className="mt-2 text-xs text-stone-500">Final artwork is proofed with you before burning. {40 - engravingText.length} characters remaining.</p>
                        </div>
                      </div>
                      <div className="mt-5">
                        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-500/90">Lettering Style</p>
                        <div className="mt-3 grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                          {ENGRAVING_FONTS.map((f) => (
                            <button
                              key={f.id}
                              type="button"
                              data-testid={`font-chip-${f.id}`}
                              onClick={() => setEngravingFont(f)}
                              className={`rounded-xl border p-3.5 text-center transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 ${
                                engravingFont.id === f.id
                                  ? "border-amber-500/70 bg-amber-950/30 shadow-ember-sm"
                                  : "border-stone-800 bg-[#141110] hover:border-amber-500/35"
                              }`}
                            >
                              <span className="block text-lg leading-tight text-amber-100" style={f.style}>Aa</span>
                              <span className="mt-2 block font-mono text-[9px] uppercase tracking-[0.18em] text-stone-400">{f.name}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </StepBlock>

            <StepBlock index={5} title="Finishing Touch" hint="Optional protection for open terraces and lakeside winds.">
              <OptionCard
                testId="config-wind-guard"
                selected={windGuard}
                onClick={() => setWindGuard(!windGuard)}
                title="Tempered Glass Wind Guard"
                desc="A low-profile flame guard in tempered glass — safety without hiding the fire."
                price={WIND_GUARD_PRICE}
              />
            </StepBlock>
          </div>

          <div className="lg:sticky lg:top-28">
            <Reveal>
              <aside data-testid="config-summary" className="overflow-hidden rounded-2xl border border-amber-500/25 bg-[#141110] shadow-ember">
                <div className="relative h-40 overflow-hidden">
                  <img src={IMG.heroTable} alt="Your commissioned fire table" className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#141110] to-transparent" />
                  <p className="absolute bottom-3 left-5 font-mono text-[10px] uppercase tracking-[0.28em] text-amber-400">Your Commission</p>
                </div>
                <div className="space-y-3 p-6">
                  {Object.entries({ Barrel: barrel.name, Tabletop: stone.name, Burner: `${shape.name} · 65k BTU`, Media: media.name, Engraving: engraving.name, "Wind Guard": windGuard ? "Included" : "—" }).map(([k, v]) => (
                    <div key={k} className="flex items-baseline justify-between gap-4 border-b border-stone-800/60 pb-2.5 text-sm">
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">{k}</span>
                      <span className="text-right text-stone-200">{v}</span>
                    </div>
                  ))}
                  <div className="flex items-baseline justify-between pt-2">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Estimated Investment</span>
                    <AnimatePresence mode="popLayout">
                      <motion.span
                        key={total}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.35, ease: EASE }}
                        data-testid="config-total-price"
                        className="font-serif text-3xl text-gold-gradient"
                      >
                        {fmtCAD(total)}
                      </motion.span>
                    </AnimatePresence>
                  </div>
                  <p className="text-xs text-stone-500">CAD · Final quote confirmed in consultation. Delivery across Canada & the US.</p>
                </div>

                <div className="border-t border-stone-800/70 p-6">
                  {done ? (
                    <div data-testid="config-success" className="rounded-xl border border-amber-500/40 bg-amber-950/20 p-5 text-center">
                      <ShieldCheck size={26} className="mx-auto text-amber-400" />
                      <p className="mt-3 font-serif text-lg text-stone-100">Received by the atelier.</p>
                      <p className="mt-2 text-xs leading-relaxed text-stone-400">
                        Your commission brief is on its way to our workshop. Expect a personal reply at {form.email} within one business day.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={submit} className="space-y-3" data-testid="config-inquiry-form">
                      <input data-testid="config-form-name" required minLength={2} placeholder="Full name" className={INPUT_CLS} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                      <input data-testid="config-form-email" required type="email" placeholder="Email address" className={INPUT_CLS} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                      <input data-testid="config-form-phone" type="tel" placeholder="Phone (optional)" className={INPUT_CLS} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                      <textarea data-testid="config-form-message" rows={3} placeholder="Tell us about your property, timeline, or vision…" className={`${INPUT_CLS} resize-none`} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                      <GoldButton data-testid="config-form-submit" type="submit" disabled={sending} className="w-full">
                        {sending ? <Loader2 size={15} className="animate-spin" /> : <>Request This Commission <ArrowRight size={15} /></>}
                      </GoldButton>
                    </form>
                  )}
                </div>
              </aside>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
}
